import { useState, useEffect } from 'react';
import api from '../services/api';
import { trackEvent } from '../services/analytics';

const STATUS_CONFIG = {
  pending:     { label: 'Needs Review', color: '#d4a843', bg: 'rgba(212,168,67,0.1)',  icon: '⏳' },
  sent:        { label: 'Sent',         color: '#5b9bd5', bg: 'rgba(91,155,213,0.1)',   icon: '📨' },
  resolved:    { label: 'Resolved',     color: '#6bb87a', bg: 'rgba(107,184,122,0.1)',  icon: '✓' },
  followed_up: { label: 'Followed Up',  color: '#34d399', bg: 'rgba(52,211,153,0.1)',   icon: '🔄' },
  converted:   { label: 'Converted ★',  color: '#3a9e6e', bg: 'rgba(58,158,110,0.15)', icon: '⭐' },
  expired:     { label: 'Expired',      color: '#888',    bg: 'rgba(136,136,136,0.1)',  icon: '—' },
  skipped:     { label: 'Skipped',      color: '#888',    bg: 'rgba(136,136,136,0.1)',  icon: '—' },
};

const CATEGORY_LABELS = {
  service_speed: 'Slow Service',
  staff_attitude: 'Staff Attitude',
  product_quality: 'Product Quality',
  cleanliness: 'Cleanliness',
  pricing: 'Pricing',
  communication: 'Communication',
  booking_issue: 'Booking Issue',
  noise_comfort: 'Noise / Comfort',
  amenities: 'Amenities',
  accuracy: 'Order Accuracy',
  other: 'Other',
};

const RATING_COLORS = {
  1: '#d44343',
  2: '#e8734a',
  3: '#d4a843',
};

const FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Needs Review' },
  { value: 'sent', label: 'Sent' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'converted', label: 'Converted' },
];


/* ─── Stat Card ─── */
function StatCard({ label, value, accent, sub }) {
  return (
    <div className="card" style={{ padding: 20 }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={accent ? { color: accent } : {}}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}


/* ─── Recovery Card ─── */
function RecoveryCard({ recovery, onAction, actionLoading }) {
  const [editing, setEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState(recovery.recovery_message || '');
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  const status = STATUS_CONFIG[recovery.status] || STATUS_CONFIG.pending;
  const ratingColor = RATING_COLORS[recovery.original_rating] || '#d4a843';
  const categoryLabel = CATEGORY_LABELS[recovery.complaint_category] || recovery.complaint_category;

  const handleSaveEdit = async () => {
    await onAction(recovery.id, 'edit', { recovery_message: editedMessage });
    setEditing(false);
  };

  const handleResolve = async () => {
    await onAction(recovery.id, 'resolve', notes ? { owner_notes: notes } : {});
    setShowNotes(false);
  };

  return (
    <div className="card" style={{
      padding: 0,
      overflow: 'hidden',
      opacity: ['skipped', 'expired'].includes(recovery.status) ? 0.6 : 1,
      transition: 'opacity 0.2s, transform 0.2s',
    }}>
      {/* Header bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 20px',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Rating dot */}
          <span style={{
            width: 28, height: 28, borderRadius: '50%',
            background: ratingColor,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontSize: 12, fontWeight: 700,
          }}>
            {recovery.original_rating}
          </span>
          <div>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>
              {recovery.customer_name || 'Anonymous customer'}
            </span>
            {recovery.customer_email && (
              <span style={{ fontSize: 11, color: 'var(--text-light)', marginLeft: 8 }}>
                {recovery.customer_email}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Category pill */}
          {recovery.complaint_category && recovery.complaint_category !== 'other' && (
            <span style={{
              padding: '3px 10px',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              fontSize: 11,
              color: 'var(--text-light)',
            }}>
              {categoryLabel}
            </span>
          )}
          {/* Status badge */}
          <span style={{
            padding: '4px 12px',
            background: status.bg,
            borderRadius: 12,
            fontSize: 12,
            fontWeight: 600,
            color: status.color,
          }}>
            {status.icon} {status.label}
          </span>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: 20 }}>
        {/* Original complaint */}
        <div style={{
          padding: '12px 16px',
          background: 'var(--bg)',
          borderRadius: 8,
          borderLeft: `3px solid ${ratingColor}`,
          marginBottom: 16,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.3px' }}>
            Customer feedback
          </div>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
            {recovery.original_feedback}
          </p>
        </div>

        {/* AI recovery message */}
        {recovery.recovery_message && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                Recovery message
              </span>
              {recovery.status === 'pending' && !editing && (
                <button
                  onClick={() => setEditing(true)}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 12, color: 'var(--accent)', fontWeight: 500,
                  }}
                >
                  Edit
                </button>
              )}
            </div>
            {editing ? (
              <div>
                <textarea
                  value={editedMessage}
                  onChange={(e) => setEditedMessage(e.target.value)}
                  style={{
                    width: '100%',
                    minHeight: 100,
                    padding: '12px 14px',
                    borderRadius: 8,
                    border: '1px solid var(--border)',
                    background: 'var(--bg)',
                    color: 'var(--text)',
                    fontSize: 13,
                    lineHeight: 1.6,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button className="btn btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                    Save
                  </button>
                  <button
                    className="btn"
                    onClick={() => { setEditing(false); setEditedMessage(recovery.recovery_message); }}
                    style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div style={{
                padding: '12px 16px',
                background: 'rgba(52,211,153,0.04)',
                border: '1px solid rgba(52,211,153,0.15)',
                borderRadius: 8,
              }}>
                <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text)', margin: 0 }}>
                  {recovery.recovery_message}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Action suggestion */}
        {recovery.action_suggestion && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '10px 14px',
            background: 'rgba(52,211,153,0.06)',
            borderRadius: 8,
            marginBottom: 16,
          }}>
            <span style={{ fontSize: 14, flexShrink: 0 }}>💡</span>
            <span style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.5 }}>
              <strong>Suggested action:</strong> {recovery.action_suggestion}
            </span>
          </div>
        )}

        {/* Owner notes display */}
        {recovery.owner_notes && (
          <div style={{
            padding: '10px 14px',
            background: 'var(--bg)',
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 12,
            color: 'var(--text-light)',
          }}>
            <strong>Your notes:</strong> {recovery.owner_notes}
          </div>
        )}

        {/* Resolution notes input */}
        {showNotes && (
          <div style={{ marginBottom: 16 }}>
            <textarea
              placeholder="Optional: Add notes about how you resolved this (e.g. 'Offered free replacement, customer was happy')"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{
                width: '100%',
                minHeight: 70,
                padding: '10px 14px',
                borderRadius: 8,
                border: '1px solid var(--border)',
                background: 'var(--bg)',
                color: 'var(--text)',
                fontSize: 13,
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
        )}

        {/* Action buttons — vary by status */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {recovery.status === 'pending' && (
            <>
              <button
                className="btn btn-primary"
                onClick={() => onAction(recovery.id, 'send')}
                disabled={actionLoading || !recovery.has_email}
                title={!recovery.has_email ? 'No email address — copy the message and send manually' : ''}
              >
                {actionLoading ? 'Sending...' : '📨 Send Recovery'}
              </button>
              {!recovery.has_email && (
                <button
                  className="btn"
                  onClick={() => {
                    navigator.clipboard.writeText(recovery.recovery_message);
                  }}
                  style={{ background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)' }}
                >
                  📋 Copy Message
                </button>
              )}
              <button
                className="btn"
                onClick={() => onAction(recovery.id, 'skip')}
                disabled={actionLoading}
                style={{ background: 'none', border: '1px solid var(--border)', color: 'var(--text-light)', fontSize: 12 }}
              >
                Skip
              </button>
            </>
          )}

          {recovery.status === 'sent' && (
            <>
              {!showNotes ? (
                <button
                  className="btn btn-primary"
                  onClick={() => setShowNotes(true)}
                >
                  ✓ Mark Resolved
                </button>
              ) : (
                <button
                  className="btn btn-primary"
                  onClick={handleResolve}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Saving...' : 'Confirm Resolved'}
                </button>
              )}
            </>
          )}

          {recovery.status === 'resolved' && recovery.has_email && (
            <button
              className="btn btn-primary"
              onClick={() => onAction(recovery.id, 'followup')}
              disabled={actionLoading}
            >
              {actionLoading ? 'Sending...' : '🔄 Send Review Request'}
            </button>
          )}

          {['resolved', 'followed_up'].includes(recovery.status) && (
            <button
              className="btn"
              onClick={() => onAction(recovery.id, 'convert')}
              disabled={actionLoading}
              style={{
                background: 'rgba(52,211,153,0.1)',
                border: '1px solid rgba(52,211,153,0.3)',
                color: '#34d399',
                fontWeight: 600,
              }}
            >
              ⭐ Customer Left a Review
            </button>
          )}
        </div>

        {/* Timestamp footer */}
        <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 12, opacity: 0.6 }}>
          {recovery.created_at && `Created ${new Date(recovery.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
          {recovery.sent_at && ` · Sent ${new Date(recovery.sent_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
          {recovery.resolved_at && ` · Resolved ${new Date(recovery.resolved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
          {recovery.converted_at && ` · Converted ${new Date(recovery.converted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
        </div>
      </div>
    </div>
  );
}


/* ─── Empty State ─── */
function EmptyState({ filter }) {
  if (filter !== 'all') {
    return (
      <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
        <div style={{ fontSize: 14, color: 'var(--text-light)' }}>
          No {FILTER_OPTIONS.find(f => f.value === filter)?.label?.toLowerCase() || ''} recoveries
        </div>
      </div>
    );
  }

  return (
    <div className="card" style={{ padding: '48px 24px', textAlign: 'center' }}>
      <div style={{ fontSize: 40, marginBottom: 16 }}>🔄</div>
      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 8, color: 'var(--text)' }}>
        No recovery opportunities yet
      </h3>
      <p style={{ fontSize: 13, color: 'var(--text-light)', maxWidth: 400, margin: '0 auto', lineHeight: 1.6 }}>
        When customers leave 1-3 star feedback through your review funnel, recovery opportunities will appear here
        with AI-drafted messages ready to send.
      </p>
      <p style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 16 }}>
        Make sure your <a href="/settings" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>Smart Feedback Funnel</a> is
        set up and your QR code / link is shared with customers.
      </p>
    </div>
  );
}


/* ─── Page Styles ─── */
function RecoveryStyles() {
  return (
    <style>{`
      .recovery-stats-grid {
        display: grid;
        grid-template-columns: repeat(5, 1fr);
        gap: 12px;
        margin-bottom: 24px;
      }
      @media (max-width: 768px) {
        .recovery-stats-grid {
          grid-template-columns: repeat(2, 1fr);
        }
      }
      @media (max-width: 480px) {
        .recovery-stats-grid {
          grid-template-columns: 1fr;
        }
      }
    `}</style>
  );
}


/* ─── Main Page ─── */
export default function RecoveryPage() {
  const [recoveries, setRecoveries] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [recoveriesData, statsData] = await Promise.all([
        api.get('/recovery' + (filter !== 'all' ? `?status=${filter}` : '')),
        api.get('/recovery/stats'),
      ]);
      setRecoveries(recoveriesData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load recoveries:', err);
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filter]);

  const handleAction = async (recoveryId, action, body = {}) => {
    setActionLoading(recoveryId);
    try {
      if (action === 'edit') {
        await api.put(`/recovery/${recoveryId}`, body);
      } else {
        await api.post(`/recovery/${recoveryId}/${action}`, body);
      }
      await fetchData();
      trackEvent('recovery_action', { action, recoveryId });
    } catch (err) {
      console.error(`Recovery action ${action} failed:`, err);
      alert(`Failed to ${action}: ${err.response?.data?.detail || err.message}`);
    }
    setActionLoading(null);
  };

  return (
    <div>
      <RecoveryStyles />

      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Review Recovery</h1>
          <p className="page-subtitle">
            Turn unhappy customers into your best advocates
          </p>
        </div>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="recovery-stats-grid">
          <StatCard label="Pending" value={stats.pending} accent="#d4a843" />
          <StatCard label="Sent" value={stats.sent} accent="#5b9bd5" />
          <StatCard label="Resolved" value={stats.resolved} accent="#6bb87a" />
          <StatCard
            label="Converted"
            value={stats.converted}
            accent="#34d399"
            sub={stats.converted > 0 ? `${stats.converted} negative → positive` : null}
          />
          <StatCard
            label="Conversion Rate"
            value={`${stats.conversion_rate}%`}
            accent="#3a9e6e"
            sub={stats.sent > 0 ? `of ${stats.sent} sent` : null}
          />
        </div>
      )}

      {/* Category breakdown (if data exists) */}
      {stats?.categories?.length > 0 && (
        <div className="card" style={{ padding: '16px 20px', marginBottom: 24 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
              Top issues:
            </span>
            {stats.categories.slice(0, 5).map((cat) => (
              <span key={cat.category} style={{
                padding: '4px 12px',
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 16,
                fontSize: 12,
                color: 'var(--text-light)',
              }}>
                {CATEGORY_LABELS[cat.category] || cat.category} <strong style={{ color: 'var(--text)' }}>({cat.count})</strong>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div className="filter-tabs" style={{ marginBottom: 24 }}>
        {FILTER_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`filter-tab ${filter === opt.value ? 'active' : ''}`}
            onClick={() => setFilter(opt.value)}
          >
            {opt.label}
            {opt.value === 'pending' && stats?.pending > 0 && (
              <span style={{
                marginLeft: 6,
                padding: '1px 7px',
                background: '#d4a843',
                color: '#fff',
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 700,
              }}>
                {stats.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Recovery cards */}
      {loading ? (
        <div className="loading">Loading recoveries...</div>
      ) : recoveries.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {recoveries.map((recovery) => (
            <RecoveryCard
              key={recovery.id}
              recovery={recovery}
              onAction={handleAction}
              actionLoading={actionLoading === recovery.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
